import { ImageLoaderManager } from '@/commons/assetsManager/ImageLoaderManager';
import { useTemplateStore } from '@/store/template.store';
import { FABRIC_CONTROL_COLOR, FONT_SIZE_MULTIPLIER, GRADUATION_NAME } from '@/littleWedding/projectConstantes';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/littleWedding/fabricGroupsName';
import { addCuttingPathField, addSymetricalPathField } from '@/littleWedding/CutFieldController';
import { getFabricGroupById } from '@/commons/fabricjs/FabricGroupHelper';
import { WTextbox } from '@/commons/fabricjs/WTextbox';
import { isFieldOnSide } from '@/utils/fieldSide';
import { Canvas, FabricImage, FabricText, type FabricObject, type Group, StaticCanvas, type Textbox } from 'fabric';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';
import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';
import type { IGroup } from '@/typescript/interfaces/IGroup';

// Preload image that will be cached
ImageLoaderManager.loadImage('./images/rotate-alt.svg');

// Remove (and dispose) every fabric object in `container` that shares the given `id`.
// Used to enforce idempotent add-by-id semantics across canvas and group containers,
// preventing the BAT triplication bug already addressed by commit 28e465a.
function removeExistingObjectsById(container: Canvas | Group | null | undefined, id: string): void {
    if (!container) return;
    const existing = container.getObjects().filter((o) => (o as FabricObject & { id?: string }).id === id);
    existing.forEach((obj) => {
        container.remove(obj);
        obj.dispose?.();
    });
}

async function addMultipleFields(
    fields: (IImageField | ITextboxField | ITextField)[],
    params: {
        addToCanvas?: boolean;
        fabricWorldInstance: WFabricWorld;
        isAdmin?: boolean;
        isProductEdition?: boolean;
        select?: boolean;
        selectable?: boolean;
    },
) {
    const { addToCanvas = true, fabricWorldInstance, select = false, selectable = true } = params;
    const promiseArray: Promise<IFabricImageCustom | IFabricTextCustom | ITextboxCustom | undefined>[] = [];
    fields.forEach((field) => {
        if ((field as ITextField)?.text || (field as ITextField)?.defaultText) {
            if ('size' in field.transform) {
                const newTextboxPromise = addTextboxField(field as ITextboxField, {
                    fabricWorldInstance,
                    selectable,
                    addToCanvas,
                    select,
                    isAdmin: params.isAdmin,
                });
                promiseArray.push(newTextboxPromise);
                return;
            }
            const newTextPromise = addTextField(field as ITextField, {
                fabricWorldInstance,
                selectable: field.editable ? selectable : false,
                addToCanvas,
                select,
                isAdmin: params.isAdmin,
            });
            promiseArray.push(newTextPromise);
        } else if ((field as IImageField)?.uploadType === 'cutPath') {
            if (isFieldOnSide(field, 'recto')) {
                addCuttingPathField(field as IImageField, fabricWorldInstance, params.isAdmin, params.isProductEdition);
            } else {
                // verso
                addSymetricalPathField(field as IImageField, fabricWorldInstance, params.isAdmin, params.isProductEdition);
            }
        } else {
            const newImagePromise = addImageField(field, {
                fabricWorldInstance,
                selectable: field.editable ? selectable : false,
                addToCanvas,
                select,
                isAdmin: params.isAdmin,
            });
            promiseArray.push(newImagePromise);
        }
    });
    const results = await Promise.all(promiseArray);
    results.forEach((result) => {
        if (result) {
            setRotationIconOnControl(result);
        }
    });
    return results;
}

function addTextField(
    textField: ITextField,
    params: {
        addToCanvas: boolean;
        fabricWorldInstance: WFabricWorld;
        isAdmin?: boolean;
        select: boolean;
        selectable: boolean;
    },
): Promise<IFabricTextCustom | undefined> {
    const { addToCanvas = true, fabricWorldInstance, select = true, selectable = true } = params;
    const { fabricCanvas } = fabricWorldInstance;
    if (!textField || !fabricCanvas) return Promise.resolve(undefined);
    const fText = textFieldToFText(textField, {
        fabricWorldInstance,
        FabricTextClass: FabricText,
        selectable,
    });
    if (!fText) return Promise.resolve(undefined);
    centerToCanvas(fText, {
        field: textField,
        fabricCanvas,
        fabricWorldInstance: fabricWorldInstance,
    });

    if (addToCanvas) {
        removeExistingObjectsById(fabricCanvas, textField.id);
        const lockedGroup = getFabricGroupById(fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        removeExistingObjectsById(lockedGroup, textField.id);
        lockedGroup?.set('dirty', true);
        fabricCanvas?.add(fText);
    }
    if (selectable && select) {
        fabricCanvas?.setActiveObject(fText);
    }
    return Promise.resolve(fText as IFabricTextCustom);
}

function addTextboxField(
    textboxField: ITextboxField,
    params: {
        addToCanvas: boolean;
        fabricWorldInstance: WFabricWorld;
        isAdmin?: boolean;
        select: boolean;
        selectable: boolean;
    },
) {
    const { addToCanvas = true, fabricWorldInstance, select = true, selectable = true } = params;
    const { fabricCanvas } = fabricWorldInstance;
    if (!textboxField) return Promise.resolve(undefined);
    const fTextbox = textFieldToFText(textboxField, {
        fabricWorldInstance,
        FabricTextClass: WTextbox,
        selectable,
        isAdmin: params.isAdmin,
    });
    if (!fTextbox || !fabricCanvas) return Promise.resolve(undefined);
    fTextbox.setControlsVisibility({
        tl: false, // top-left
        tr: false, // top-right
        bl: false, // bottom-left
        br: false, // bottom-right
        mt: false, // middle-top
        mb: false, // middle-bottom
    });
    centerToCanvas(fTextbox, {
        field: textboxField,
        fabricCanvas,
        fabricWorldInstance,
    });

    fTextbox.set('dirty', true);

    if (addToCanvas) {
        removeExistingObjectsById(fabricCanvas, textboxField.id);
        const lockedGroup = getFabricGroupById(fabricCanvas, LOCKED_FOREGROUND_GROUP_NAME);
        removeExistingObjectsById(lockedGroup, textboxField.id);
        lockedGroup?.set('dirty', true);
        fabricCanvas?.add(fTextbox);
    }
    if (selectable && select && textboxField.editable) {
        fabricCanvas?.setActiveObject(fTextbox);
    }
    return Promise.resolve(fTextbox as ITextboxCustom);
}

async function addImageField(
    imageField: IImageField,
    params: {
        addToCanvas: boolean;
        canvas?: Canvas; // Allow custom canvas
        fabricWorldInstance: WFabricWorld;
        isAdmin?: boolean;
        select?: boolean;
        selectable?: boolean;
        smoothImage?: boolean;
    },
): Promise<IFabricImageCustom | undefined> {
    const { addToCanvas = true, fabricWorldInstance, select = true, selectable = true, smoothImage = true } = params;
    const { fabricCanvas } = fabricWorldInstance;
    if (!imageField || !fabricCanvas) return Promise.resolve(undefined);
    let fImage: FabricImage;
    if (!imageField.url) return Promise.resolve(undefined);
    const loadImage = ImageLoaderManager.loadImage(imageField.url);
    return loadImage
        .then((img) => {
            if (img instanceof Error) {
                return img;
            }
            if (imageField?.uploadType === 'cutPath') {
                // TODO: implement cutting path loading
                // fImage = addCuttingPathField(img);
            } else {
                fImage = new FabricImage(img, {
                    cornerColor: FABRIC_CONTROL_COLOR,
                    cornerStrokeColor: FABRIC_CONTROL_COLOR,
                    id: imageField.id,
                    left: imageField.transform.position.x,
                    top: imageField.transform.position.y,
                    angle: imageField.transform.angle,
                    scaleX: imageField.transform.scale.x,
                    scaleY: imageField.transform.scale.y,
                    editable: imageField.editable,
                    selectable: (imageField.editable ? params.selectable : false) || params.isAdmin,
                    lockMovementY: imageField?.filename === GRADUATION_NAME ? true : false,
                    imageSmoothing: smoothImage,
                    frameSize: {
                        width: fabricCanvas.width,
                        height: fabricCanvas.height,
                    },
                    hasControls: imageField?.filename === GRADUATION_NAME ? false : true,
                    perPixelTargetFind: imageField.filename !== GRADUATION_NAME,
                });
                if (imageField?.filename === GRADUATION_NAME) {
                    fabricWorldInstance.objectConstrainId.push(imageField.id);
                }
            }
            // TODO: for future behavior, carefull to parametric grid field
            if (addToCanvas) {
                const mainImgGroup = (fabricCanvas?.getObjects('group') as IGroup[]).find((group) => {
                    return group?.id === 'mainImgGroupName';
                });
                removeExistingObjectsById(fabricCanvas, imageField.id);
                removeExistingObjectsById(mainImgGroup, imageField.id);
                if (mainImgGroup) {
                    mainImgGroup.add(fImage);
                    mainImgGroup.set('dirty', true);
                } else {
                    fabricCanvas.add(fImage);
                }
                centerToCanvas(fImage as IFabricImageCustom, {
                    field: imageField,
                    fabricCanvas,
                    fabricWorldInstance,
                });
                if (imageField.filename === GRADUATION_NAME) {
                    const scaleFactor = fabricWorldInstance.imageRenderHeight / fImage.height;
                    fImage.set('top', 0);
                    fImage.set('scaleX', scaleFactor);
                    fImage.set('scaleY', scaleFactor);
                    fImage.set('name', GRADUATION_NAME);
                    fImage.setCoords();
                    fImage.set('dirty', true);
                }
            }
            if (selectable && select) {
                fabricCanvas.setActiveObject(fImage);
            }
            return fImage;
        })
        .then(() => {
            return fImage as IFabricImageCustom;
        });
}

function textFieldToFText<T extends WFabricWorld, U extends FabricText | Textbox>(
    field: ITextboxField | ITextField,
    params: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        FabricTextClass: new (...args: any[]) => U;
        fabricWorldInstance: T;
        isAdmin?: boolean;
        selectable: boolean;
    },
): IFabricTextCustom | ITextboxCustom | null {
    const { fabricCanvas, fontSizeMultiplier } = params.fabricWorldInstance;
    if (!fabricCanvas) return null;
    const { height, width } = fabricCanvas;
    let textboxWidth = 0;
    let textboxHeight = 0;
    const charSpacing = (field.characterSpacing ?? 0) * fontSizeMultiplier * 4;
    if (field.transform?.size !== undefined) {
        ({ height: textboxHeight, width: textboxWidth } = field.transform.size);
    }
    const lineSpacingOffset = 16 / (FONT_SIZE_MULTIPLIER * 4);
    const fText = new params.FabricTextClass(field.text ?? field.defaultText, {
        name: 'textName',
        id: field.id,
        cornerColor: FABRIC_CONTROL_COLOR,
        cornerStrokeColor: FABRIC_CONTROL_COLOR,
        editable: field.editable,
        frameSize: {
            width,
            height,
        },
        objectCaching: false,
        fill: field.color,
        fontFamily: field.font,
        fontSize: field.fontSize * fontSizeMultiplier,
        fontWeight: field.isBold ? 'bold' : 'normal',
        fontStyle: field.isItalic ? 'italic' : 'normal',
        left: field.transform.position.x,
        top: field.transform.position.y,
        angle: field.transform.angle,
        scaleX: field.transform.scale.x,
        scaleY: field.transform.scale.y,
        underline: field.isUnderlined,
        lineHeight: (field.lineSpacing === 0 ? 0.01 : (field.lineSpacing ?? 1) / (FONT_SIZE_MULTIPLIER * 4)) + lineSpacingOffset,
        charSpacing,
        textAlign: field.textAlign ?? 'left',
        centeredRotation: true,
        selectable: (field.editable ? params.selectable : false) || params.isAdmin,
        ...(textboxWidth + textboxHeight !== 0 ? { width: textboxWidth, height: textboxHeight } : {}),
        splitByGrapheme: false,
        styles: field.styles || {},
    });

    return fText as IFabricTextCustom | ITextboxCustom;
}

function centerToCanvas(
    fObject: IFabricImageCustom | IFabricTextCustom | ITextboxCustom,
    params: {
        fabricCanvas: StaticCanvas;
        fabricWorldInstance?: WFabricWorld;
        field: IImageField | ITextboxField | ITextField;
    },
) {
    if (fObject.left + fObject.top === 0) {
        const left = ((params.fabricWorldInstance?.imageRenderWidth ?? params.fabricCanvas.width) - (fObject.width || 0)) / 2;
        const top = ((params.fabricWorldInstance?.imageRenderHeight ?? params.fabricCanvas.height) - (fObject.height || 0)) / 2;
        fObject.set('left', left);
        fObject.set('top', top);
        fObject.setCoords();
        fObject.set('dirty', true);
        const { updateField } = useTemplateStore();
        const { field } = params;
        field.transform.position.x = fObject.left;
        field.transform.position.y = fObject.top;
        field.transform.size = {
            width: fObject.width,
            height: fObject.height,
        };
        updateField(field);
        params.fabricCanvas.renderAll();
    }
}

function setRotationIconOnControl(fObject: IFabricImageCustom | IFabricTextCustom | ITextboxCustom) {
    fObject.controls.mtr.render = function (ctx, left, top) {
        const { x, y } = this;
        ImageLoaderManager.loadImage('./images/rotate-alt.svg').then((rotationIcon) => {
            if (rotationIcon instanceof Error) {
                console.error('Error loading image:', rotationIcon);
                return;
            }
            const { height, width } = rotationIcon;
            ctx.save();
            ctx.translate(left - width / 2, top - height / 2);
            ctx.drawImage(rotationIcon, x, y);
            ctx.restore();
        });
    };
}

export { addImageField, addMultipleFields, addTextField, centerToCanvas, textFieldToFText };
